import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAppEvent } from '~/types/event'

import Button from '~/components/Buttons/Button'
import DateInput from '~/components/Inputs/ReactHookForm/DateInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useCreateAppEventMutation, useUpdateAppEventMutation } from '~/queries/events'
import { APP_EVENTS } from '~/types/event'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

const MODAL_ID = 'manage-app-event-modal'

export type TModalType = 'create' | 'edit'

export interface Values {
  date: string
  eventType: string
  message: string
}

const validationSchema = Yup.object().shape({
  date: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  eventType: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  message: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

const defaultCreateValues: Values = {
  date: new Date().toISOString(),
  eventType: '',
  message: '',
}

interface FormProps {
  appKey: string
  deviceType: string
  event?: IAppEvent
  modalType: TModalType
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
}

const ManageAppEventForm = ({
  appKey,
  deviceType,
  event,
  modalType,
  onClose,
  onFailure,
  onSuccess,
}: FormProps): ReactElement => {
  /** Local state */

  const defaultValues: Values =
    modalType === 'edit' && event
      ? {
          date: dayjs(event.date).format(),
          eventType: event.event_type,
          message: event.value,
        }
      : defaultCreateValues

  const methods = useForm<Values>({ defaultValues, resolver: yupResolver(validationSchema) })

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
  } = methods

  const eventTypeOptions: IOption[] = Object.entries(APP_EVENTS).map(([key, { label }]) => ({ label, value: key }))

  /** Queries & Mutations */

  const { mutateAsync: createAppEvent } = useCreateAppEventMutation()

  const { mutateAsync: updateAppEvent } = useUpdateAppEventMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    try {
      if (modalType === 'create') {
        await createAppEvent({
          appKey,
          date: dayjs(values.date).valueOf(),
          deviceType,
          eventType: values.eventType,
          value: values.message,
        })
      } else if (modalType === 'edit' && event) {
        await updateAppEvent({
          appKey,
          date: dayjs(values.date).valueOf(),
          deviceType,
          eventId: event.id,
          eventType: values.eventType,
          value: values.message,
        })
      }

      onSuccess()
    } catch {
      onFailure()
    }
  }

  const handleSelectFieldChange = (field: keyof Values, value: IOption | null) => {
    setValue(field, value ? value.value : '', {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  /** Render */

  return (
    <>
      <form className="manage-app-event-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody className="manage-app-event-form__inputs">
          <RequiredInformation />
          <Controller
            control={control}
            name="date"
            render={({ field: { onChange, value } }) => (
              <DateInput
                error={errors.date?.message}
                label="Event date"
                onDateChange={onChange}
                placeholder="Select a date"
                required
                value={value}
                withTime
              />
            )}
          />
          <Controller
            control={control}
            name="eventType"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors.eventType?.message}
                label="Event type"
                onChange={(value: unknown) => handleSelectFieldChange('eventType', value as IOption | null)}
                options={eventTypeOptions}
                placeholder="Select an event type"
                required
                value={eventTypeOptions.find(({ value }) => value === field.value)}
              />
            )}
          />
          <TextArea
            {...register('message')}
            error={errors.message?.message}
            label="Message"
            placeholder="Enter a message"
            required
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            Save
          </Button>
        </ModalFooter>
      </form>
      <DevTool control={control} />
    </>
  )
}

interface Props extends FormProps {
  isOpen: boolean
}

const ManageAppEventModal = ({ isOpen, modalType, onClose, ...formProps }: Props): ReactElement => {
  return (
    <Modal className="manage-app-event-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{modalType === 'create' ? 'Create' : 'Edit'} an event</ModalHeader>
      <ManageAppEventForm modalType={modalType} onClose={onClose} {...formProps} />
    </Modal>
  )
}

export default ManageAppEventModal
